// src/components/overview/QuickSyncOverview.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { triggerSync, triggerFastContentSync, resetStuckSyncs } from '../../api/syncApi';
import { fetchCourses } from '../../api/adminApi';
import { toast } from 'react-toastify';
import useAuth from '../../hooks/useAuth';
import { useSocketContext } from '../../context/SocketContext';
import SyncService from '../../services/SyncService';
import { getStoredUsername } from '../../utils/storedUsername';

/* ── Sync types ──────────────────────────────────────────── */
const SYNC_TYPES = [
  { key: 'foundation_courses', runType: 'foundation-course-sync', icon: 'bi-mortarboard',     label: 'Sync Foundation Courses' },
  { key: 'jee_courses',        runType: 'jee-course-sync',        icon: 'bi-rocket-takeoff',  label: 'Sync JEE Courses' },
  { key: 'courses',           runType: 'course-sync',            icon: 'bi-book',              label: 'Sync Courses' },
  { key: 'chapters',          runType: 'chapter-sync',           icon: 'bi-list-nested',       label: 'Sync Chapters' },
  { key: 'content',           runType: 'content-sync',           icon: 'bi-file-earmark-text', label: 'Sync Chapter Content' },
  { key: 'mentors',           runType: 'mentor-sync',            icon: 'bi-person',            label: 'Sync Mentors' },
  { key: 'exams',             runType: 'exams-sync',             icon: 'bi-card-list',         label: 'Sync Exams' },
  { key: 'attempts',          runType: 'attempts-sync',          icon: 'bi-clipboard-check',   label: 'Sync Attempts' },
  { key: 'batches',           runType: 'batch-sync',             icon: 'bi-people',            label: 'Sync Batches' },
  { key: 'users',             runType: 'users-sync',             icon: 'bi-people-fill',       label: 'Sync Users' },
  { key: 'attempt_answers',   runType: 'attempt-answers-sync',   icon: 'bi-record-circle',     label: 'Sync Attempt Answers' },
  { key: 'content_attempts',  runType: 'content-attempts-sync',  icon: 'bi-pencil-square',     label: 'Sync Content Attempts' },
  { key: 'user_conference',   runType: 'user-conference-sync',   icon: 'bi-people-fill',       label: 'Sync User Conference' },
  { key: 'video_conference',  runType: 'video-conference-sync',  icon: 'bi-people-fill',       label: 'Sync Video Conference' },
  { key: 'single_attempts',   runType: 'single-attempts-sync',   icon: 'bi-arrow-repeat',      label: 'Sync Each Attempt' },
];

/* ── PasswordModal (inline) ──────────────────────────────── */
const PasswordModal = ({ description, onConfirm, onCancel, isLoading }) => {
  const [password, setPassword] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirm(password);
  };

  return (
    <div className="schedule-modal-overlay" onClick={onCancel}>
      <div className="schedule-modal" onClick={(e) => e.stopPropagation()}>
        <div className="schedule-modal-header">
          <h2 className="schedule-modal-title">
            <i className="bi bi-lock" style={{ color: 'var(--accent-orange)' }} aria-hidden="true" />
            Authentication Required
          </h2>
          <button
            className="schedule-modal-close"
            onClick={onCancel}
            disabled={isLoading}
            aria-label="Close"
          >
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="schedule-modal-body">
            <p className="schedule-auth-note">{description}</p>
            <div className="schedule-form-group">
              <label className="schedule-form-label" htmlFor="qs-password">
                Password
              </label>
              <input
                id="qs-password"
                type="password"
                className="schedule-form-input"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoFocus
                disabled={isLoading}
              />
            </div>
          </div>
          <div className="schedule-modal-footer">
            <button type="button" className="qa-btn qa-btn--ghost" onClick={onCancel} disabled={isLoading}>
              Cancel
            </button>
            <button type="submit" className="qa-btn qa-btn--blue" disabled={isLoading || !password}>
              {isLoading ? (
                <>
                  <span className="schedule-spinner" aria-hidden="true" />
                  Validating…
                </>
              ) : (
                'Continue'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── FastContentSyncModal ────────────────────────────────── */
const FastContentSyncModal = ({ onClose, isLoading, setIsLoading }) => {
  const { login } = useAuth();
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [courseSearch, setCourseSearch] = useState('');
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [password, setPassword] = useState('');

  useEffect(() => {
    let cancelled = false;
    setCoursesLoading(true);
    fetchCourses(1, 100, '')
      .then((data) => {
        if (!cancelled) setCourses(data.courses || data.results || []);
      })
      .catch(() => toast.error('Failed to load courses'))
      .finally(() => { if (!cancelled) setCoursesLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const filtered = courses.filter(
    (c) =>
      c.title?.toLowerCase().includes(courseSearch.toLowerCase()) ||
      String(c.id).includes(courseSearch)
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCourseId) { toast.error('Please select a course'); return; }
    setIsLoading(true);
    try {
      const username = getStoredUsername();
      const ok = await login({ username, password });
      if (ok) {
        await triggerFastContentSync(selectedCourseId);
        toast.success(`Fast content sync started for course ${selectedCourseId}`);
        onClose();
      } else {
        toast.error('Invalid credentials. Sync operation canceled.');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start fast content sync');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="schedule-modal-overlay" onClick={onClose}>
      <div className="schedule-modal schedule-modal--lg" onClick={(e) => e.stopPropagation()}>
        <div className="schedule-modal-header">
          <h2 className="schedule-modal-title">
            <i className="bi bi-lightning-charge-fill" style={{ color: 'var(--accent-orange)' }} aria-hidden="true" />
            Fast Content Sync
          </h2>
          <button className="schedule-modal-close" onClick={onClose} disabled={isLoading} aria-label="Close">
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="schedule-modal-body">
            <p className="schedule-auth-note">
              Syncs content for a single course. Skips unchanged content and fetches chapters in
              parallel — much faster than a full sync.
            </p>
            <div className="schedule-form-group">
              <label className="schedule-form-label">Select Course</label>
              <input
                type="text"
                className="schedule-form-input"
                placeholder="Search by name or ID…"
                value={courseSearch}
                onChange={(e) => setCourseSearch(e.target.value)}
              />
            </div>
            <div className="fast-sync-course-list">
              {coursesLoading ? (
                <div className="fast-sync-empty">
                  <span className="schedule-spinner" style={{ color: 'var(--text-muted)' }} aria-hidden="true" />
                  {' '}Loading courses…
                </div>
              ) : filtered.length === 0 ? (
                <div className="fast-sync-empty">No courses found</div>
              ) : (
                filtered.map((course) => (
                  <div
                    key={course.id}
                    className={`fast-sync-course-item${selectedCourseId === course.id ? ' fast-sync-course-item--selected' : ''}`}
                    onClick={() => setSelectedCourseId(course.id)}
                  >
                    <input
                      type="radio"
                      checked={selectedCourseId === course.id}
                      onChange={() => setSelectedCourseId(course.id)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ accentColor: 'var(--accent-blue)', flexShrink: 0 }}
                    />
                    <div>
                      <div className="fast-sync-course-title">{course.title}</div>
                      <div className="fast-sync-course-id">ID: {course.id}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="schedule-form-group">
              <label className="schedule-form-label" htmlFor="qs-fast-sync-password">Password</label>
              <input
                id="qs-fast-sync-password"
                type="password"
                className="schedule-form-input"
                placeholder="Enter your password to confirm"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
          </div>
          <div className="schedule-modal-footer">
            <button type="button" className="qa-btn qa-btn--ghost" onClick={onClose} disabled={isLoading}>Cancel</button>
            <button type="submit" className="qa-btn qa-btn--orange" disabled={isLoading || !selectedCourseId || !password}>
              {isLoading ? (
                <><span className="schedule-spinner" aria-hidden="true" /> Starting…</>
              ) : (
                <><i className="bi bi-lightning-charge-fill" aria-hidden="true" /> Start Fast Sync</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── QuickSyncOverview ───────────────────────────────────── */
const QuickSyncOverview = () => {
  const { login } = useAuth();
  const { runningSyncs } = useSocketContext();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [showFastSyncModal, setShowFastSyncModal] = useState(false);

  const initiateSync = (type, label) => {
    setPendingAction({ type, label });
    setShowPasswordModal(true);
  };

  const cancelPasswordModal = () => {
    setShowPasswordModal(false);
    setPendingAction(null);
  };

  const handlePasswordConfirm = async (password) => {
    setIsLoading(true);
    try {
      const username = getStoredUsername();
      const ok = await login({ username, password });
      if (!ok) {
        toast.error('Invalid credentials. Sync operation canceled.');
        return;
      }

      if (pendingAction.type === 'all') {
        await triggerSync('all');
        toast.success('Full sync triggered successfully');
      } else if (pendingAction.type === 'reset_stuck') {
        const result = await resetStuckSyncs();
        toast.success(result.message || 'Stuck syncs have been reset');
      } else {
        await triggerSync(pendingAction.type);
        toast.success(`${pendingAction.label} sync triggered successfully`);
      }

      setShowPasswordModal(false);
      setPendingAction(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Operation failed');
    } finally {
      setIsLoading(false);
    }
  };

  const isAnySyncRunning = runningSyncs && runningSyncs.length > 0;

  return (
    <>
      <div className="theme-card quick-sync-overview">
        {/* Header */}
        <div className="quick-sync-header">
          <div className="quick-sync-title">
            <i className="bi bi-lightning-charge-fill" style={{ color: 'var(--accent-orange)' }} aria-hidden="true" />
            Quick Sync
          </div>
          <button
            className="quick-sync-more"
            onClick={() => navigate('/schedule')}
            title="Go to Schedule & Quick Actions"
          >
            All Actions
            <i className="bi bi-arrow-right" aria-hidden="true" />
          </button>
        </div>

        {/* Sync All + Reset row */}
        <div className="quick-sync-actions-row">
          <button
            className="qa-btn qa-btn--green"
            onClick={() => initiateSync('all', 'All Data')}
            disabled={isLoading}
            style={{ fontSize: '0.8125rem', padding: '7px 14px' }}
          >
            {isLoading && pendingAction?.type === 'all' ? (
              <>
                <span className="schedule-spinner" aria-hidden="true" />
                Triggering…
              </>
            ) : (
              <>
                <i className="bi bi-play-fill" aria-hidden="true" />
                Sync All
              </>
            )}
          </button>

          <button
            className="qa-btn qa-btn--orange"
            onClick={() => setShowFastSyncModal(true)}
            disabled={isLoading}
            style={{ fontSize: '0.8125rem', padding: '7px 14px' }}
          >
            <i className="bi bi-lightning-charge-fill" aria-hidden="true" />
            Fast Content Sync
          </button>

          <button
            className="qa-btn qa-btn--red"
            onClick={() => initiateSync('reset_stuck', 'Reset Stuck Syncs')}
            disabled={isLoading}
            style={{ fontSize: '0.8125rem', padding: '7px 14px' }}
          >
            <i className="bi bi-arrow-counterclockwise" aria-hidden="true" />
            Reset Stuck
            {isAnySyncRunning && (
              <span className="running-badge-dot" aria-hidden="true" style={{ marginLeft: 2 }} />
            )}
          </button>
        </div>

        {/* Sync by type chips */}
        <div className="sync-type-grid">
          {SYNC_TYPES.map(({ key, runType, icon, label }) => {
            const running = SyncService.isSyncRunning(runningSyncs, runType);
            return (
              <button
                key={key}
                className={`sync-type-btn${running ? ' sync-type-btn--running' : ''}`}
                onClick={() => initiateSync(key, label)}
                disabled={isLoading}
                title={running ? `${label} is currently running` : `Sync ${label}`}
              >
                {running ? (
                  <span className="running-badge-dot" aria-hidden="true" />
                ) : (
                  <i className={`bi ${icon}`} aria-hidden="true" />
                )}
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Password modal */}
      {showPasswordModal && pendingAction && (
        <PasswordModal
          description={
            <>
              Enter your password to continue with the{' '}
              <strong>{pendingAction.label}</strong> operation.
            </>
          }
          onConfirm={handlePasswordConfirm}
          onCancel={cancelPasswordModal}
          isLoading={isLoading}
        />
      )}

      {/* Fast Content Sync modal */}
      {showFastSyncModal && (
        <FastContentSyncModal
          onClose={() => setShowFastSyncModal(false)}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
        />
      )}
    </>
  );
};

export default QuickSyncOverview;
